import {
  Box,
  Card,
  TextField,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api/api";

export default function LevelForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [tiers, setTiers] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    xp_required: "",
    level_number: "",
    tierId: "",
  });

  const queryParams = new URLSearchParams(location.search);
  const levelId = queryParams.get("levelId");
  const isEditMode = Boolean(levelId);

  const fieldSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiInputBase-input": {
      width: "100% !important",
      textAlign: "left !important",
    },
    "& .MuiInputBase-inputMultiline": {
      width: "100% !important",
      textAlign: "left !important",
      display: "block !important",
    },
    "& textarea": {
      width: "100% !important",
      textAlign: "left !important",
    },
  };

  const numberFieldSx = {
    ...fieldSx,
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };

  const selectSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiSelect-select": {
      textAlign: "left !important",
      width: "100% !important",
    },
  };

  // 🔹 Fetch tiers
  const getTiers = async () => {
    try {
      const res = await api.getTiers();
      console.log("Result", res);
      if (res.status === 1) {
        setTiers(res.data?.tiers || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 🔹 Fetch level details (edit mode)
  const getLevelDetails = async () => {
    if (!levelId) return;

    try {
      setIsLoading(true);
      const res = await api.getLevelDetails({ level_id: levelId });

      if (res.status === 1) {
        const data = res.data;
        setFormData({
          title: data?.title || "",
          description: data?.description || "",
          xp_required: data?.xp_required || "",
          level_number: data?.level_number || "",
          tierId: data?.tierId || "",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  console.log("Tiers: ", tiers);

  useEffect(() => {
    getTiers();
    if (levelId) getLevelDetails();
  }, [levelId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const payload = {
        title: formData.title,
        description: formData.description,
        xp_required_for_completion: Number(formData.xp_required),
        level_number: Number(formData.level_number),
        display_order: Number(formData.level_number),
        tierId: formData.tierId,
      };

      let res;

      if (isEditMode) {
        res = await api.updateLevel({
          levelId,
          ...payload,
        });
      } else {
        res = await api.createLevel(payload);
      }

      if (res.status === 1) {
        navigate("/levels");
      } else {
        alert(res.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <SoftBox display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3} display="flex" justifyContent="center">
      <SoftBox
        sx={{
          width: "100%",
          maxWidth: "850px",
          mx: "auto",
        }}
      >
        <Card sx={{ border: "1px solid #f0f2f5", width: "100%" }}>
          <SoftBox p={3}>
            
            {/* HEADER */}
            <SoftBox mb={4}>
              <SoftTypography variant="h6" fontWeight="bold">
                Level Configuration
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Define level progression and XP requirements.
              </SoftTypography>
            </SoftBox>

            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              
              {/* TITLE */}
              <Box mb={3} width={"100%"}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Title
                </SoftTypography>
                <TextField
                  fullWidth={true}
                  sx={fieldSx}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Beginner Level"
                />
              </Box>

              {/* DESCRIPTION */}
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Description
                </SoftTypography>
                <TextField
                  fullWidth={true}
                  sx={fieldSx}
                  multiline
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Short description..."
                />
              </Box>

              {/* NUMBERS */}
              <Box display="flex" gap={3} mb={3}>
                <Box flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                    XP Required
                  </SoftTypography>
                  <TextField
                    fullWidth
                    type="number"
                    name="xp_required"
                    value={formData.xp_required}
                    onChange={(e) => {
                        const val = e.target.value;

                        // allow only digits (0–9)
                        if (val === "" || /^[0-9]+$/.test(val)) {
                        handleChange({
                            target: {
                                name: "xp_required",
                                value: val,
                            },
                        });
                        }
                    }}
                    onKeyDown={(e) => {
                        // block everything except digits
                        if (
                        !/[0-9]/.test(e.key) &&
                        ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",     // ✅ allow
                            "ArrowDown",
                        ].includes(e.key)
                        ) {
                        e.preventDefault();
                        }
                    }}
                    inputProps={{
                        inputMode: "numeric", // mobile numeric keyboard
                        pattern: "[0-9]*",
                    }}
                    sx={numberFieldSx}
                  />
                </Box>

                <Box flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                    Level Number
                  </SoftTypography>
                  <TextField
                    fullWidth
                    type="number"
                    name="level_number"
                    value={formData.level_number}
                    onChange={(e) => {
                        const val = e.target.value;

                        // allow only digits (0–9)
                        if (val === "" || /^[0-9]+$/.test(val)) {
                        handleChange({
                            target: {
                                name: "level_number",
                                value: val,
                            },
                        });
                        }
                    }}
                    onKeyDown={(e) => {
                        // block everything except digits
                        if (
                        !/[0-9]/.test(e.key) &&
                        ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",     // ✅ allow
                            "ArrowDown",
                        ].includes(e.key)
                        ) {
                        e.preventDefault();
                        }
                    }}
                    inputProps={{
                        inputMode: "numeric", // mobile numeric keyboard
                        pattern: "[0-9]*",
                    }}
                    sx={numberFieldSx}
                    disabled={levelId}
                  />
                </Box>
              </Box>

              {/* TIER SELECT */}
              <Box mb={4}>
                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">
                  Tier
                </SoftTypography>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    name="tierId"
                    value={formData.tierId}
                    onChange={handleChange}
                    displayEmpty
                    disabled={levelId}
                  >
                    <MenuItem value="" disabled>Select tier</MenuItem>
                    {tiers.map((tier) => (
                      <MenuItem key={tier._id} value={tier._id}>
                        {tier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* SUBMIT */}
              <Box display="flex" justifyContent="flex-end">
                <SoftButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  )}
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                    ? "Update Level"
                    : "Create Level"}
                </SoftButton>
              </Box>

            </form>
          </SoftBox>
        </Card>
      </SoftBox>
    </SoftBox>
  );
}